import useDiscoveryFetch from './useDiscoveryFetch';
import React, { useState, useMemo, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Form
} from 'react-bootstrap';
import MapGL, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import icImgPreview from '../../assets/img/ic-img.svg';
import gptwBanner from '../../assets/img/gptw-banner.svg';
import icAdress from '../../assets/img/ic-home.svg';
import icCountry from '../../assets/img/ic-globe.svg';
import icCity from '../../assets/img/ic-city.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import icLegalName from '../../assets/img/ic-building.svg';
import icNumber from '../../assets/img/ic-number.svg';
import icGlobalSize from '../../assets/img/ic-user-group.svg';
import icLocalSize from '../../assets/img/ic-user-multiple.svg';
import icRating from '../../assets/img/ic-star-square.svg';
import icStar from '../../assets/img/ic-star.svg';
import icStarGray from '../../assets/img/ic-star-gray.svg';
import icRatingSource from '../../assets/img/ic-stack-star.svg';
import icIndustry from '../../assets/img/ic-factory.svg';
import icBusinessPurpose from '../../assets/img/ic-briefcase.svg';
import icLocalActivities from '../../assets/img/ic-city-buildings.svg';
import icLocalStaffing from '../../assets/img/ic-user-square.svg';
import icRank from '../../assets/img/ic-rank.svg';
import icYear from '../../assets/img/ic-calendar.svg';
import icRevenue from '../../assets/img/ic-sack.svg';
import icPercentage from '../../assets/img/ic-graph-rise.svg';
import icProfit from '../../assets/img/ic-graph-compare.svg';
import icApproved from '../../assets/img/ic-check-circle.svg';
import icRejected from '../../assets/img/ic-close-circle.svg';
import icRLifecycleReview from '../../assets/img/ic-lyfecycle-review.svg';
import icUnderReview from '../../assets/img/ic-under-review.svg';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { MAPBOX_TOKEN } from '../../config/mapbox';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import { UserContext } from '../../UserContext';
import { isAdminType } from '../../utils/roleUtils';
import CompanyDetailHero from './companyDetail/CompanyDetailHero';
import CompanyDetailMobileTabs from './companyDetail/CompanyDetailMobileTabs';
import CompanyDetailSection from './companyDetail/CompanyDetailSection';
import InfoRow from './companyDetail/InfoRow';
import StarRating from './companyDetail/StarRating';
import JobsTabLabel from './companyDetail/JobsTabLabel';
import { useDiscoveryJobsCount } from './companyDetail/useDiscoveryJobsCount';
import { hasAnyValue, isEmptyValue } from './companyDetail/emptyValue';

const DiscoveryCompanyView = () => {
    const { id, cityId, companyId } = useParams();
    const actualCompanyId = companyId || id;
    const actualCityId = cityId || null;
    // id is the country code whenever companyId is present (context-aware routes)
    const countryCode = companyId ? (id || null) : null;
    const isCountryCityContext = Boolean(companyId && cityId && id);
    const isCountryOnlyContext = Boolean(companyId && !cityId && id);

    const { user } = useContext(UserContext);
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const isAdminViewer = isAdminType(user?.role);
    const { jobsCount } = useDiscoveryJobsCount(actualCompanyId);

    const [company, setCompany] = useState(null);
    const [cityData, setCityData] = useState(null);
    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [industriesData, setIndustriesData] = useState([]);
    const [isLoadingNace, setIsLoadingNace] = useState(true);
    const [naceError, setNaceError] = useState('');

    const approvedStatus = String(company?.accountApprovalStatus || '').trim().toLowerCase();
    const isApproved = approvedStatus === 'approved';

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCompany = async () => {
            try {
                if (!isReconnect || !company) setLoading(true);
                setError('');

                const response = await api.get(`/api/discovery/companies/${actualCompanyId}`, { signal });
                if (!isCurrent()) return;
                const companyData = response.data;

                setCompany(companyData);

                if (!isReconnect) trackEvent(ANALYTICS_EVENTS.DISCOVERY_COMPANY_VIEWED, {
                    companyId: companyData?._id || actualCompanyId,
                    companyName: companyData?.title || '',
                    country: companyData?.country || '',
                    city: companyData?.cityRegion || companyData?.city || '',
                    industry: companyData?.industries?.[0]?.industry || '',
                    level: 2,
                    source: isCountryCityContext
                        ? 'country_city_company_detail_page'
                        : isCountryOnlyContext
                            ? 'country_company_detail_page'
                            : actualCityId
                                ? 'city_company_detail_page'
                                : 'direct_company_detail_page',
                });
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching company:', err);
                setError('An error occurred while fetching company details.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCompany();
    }, [actualCompanyId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!actualCityId) return;
        api.get(`/api/discovery/cities/${actualCityId}`, { signal })
            .then(res => { if (isCurrent()) setCityData(res.data); })
            .catch(() => {});
    }, [actualCityId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!isCountryOnlyContext) return;
        api.get(`/api/discovery/countries/${countryCode}`, { signal })
            .then(res => { if (isCurrent()) setCountryData(res.data); })
            .catch(() => {});
    }, [isCountryOnlyContext, countryCode]);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {

        const fetchNace = async () => {
            try {
                if (!isReconnect || industriesData.length === 0) setIsLoadingNace(true);
                setNaceError('');

                const res = await api.get('/api/nace', { signal });
                if (!isCurrent()) return;

                const data = Array.isArray(res.data) ? res.data : [];
                setIndustriesData(data);
            } catch (err) {
                if (!isCurrent()) return;

                console.log('❌ Failed to load NACE list:', err?.message || err);
                setNaceError('Failed to load industries list.');
                setIndustriesData([]);
            } finally {
                if (!isCurrent()) return;

                setIsLoadingNace(false);
            }
        };

        fetchNace();

    }, []);

    const industriesList = useMemo(() => {
        if (!Array.isArray(company?.industries)) return [];

        return company.industries
            .filter((item) => item?.industry)
            .map((item) => ({
                industry: item.industry,
                subIndustry: Array.isArray(item.subIndustry) ? item.subIndustry : [],
                subIndustry2: Array.isArray(item.subIndustry2) ? item.subIndustry2 : [],
            }));
    }, [company]);

    const industryNameByCode = useMemo(() => {
        const m = new Map();
        (industriesData || []).forEach((ind) => m.set(ind.code, ind.name));
        return m;
    }, [industriesData]);

    const getSubIndustryNames = (industryCode, subIndustryCodes = []) => {
        const selectedIndustry = (industriesData || []).find((ind) => ind.code === industryCode);
        if (!selectedIndustry || !Array.isArray(subIndustryCodes) || !subIndustryCodes.length) {
            return 'N/A';
        }

        return subIndustryCodes
            .map((code) => {
                const sub = (selectedIndustry.subIndustries || []).find((s) => s.code === code);
                if (sub) return sub.name;

                const subSub = (selectedIndustry.subIndustries || [])
                    .flatMap((s) => s.subSubIndustries || [])
                    .find((ss) => ss.code === code);

                if (subSub) return subSub.name;

                return null;
            })
            .filter(Boolean)
            .join(', ') || 'N/A';
    };

    const logo = company?.logo || '';
    const formData = {
        title: company?.title || '',
        accessLevel: company?.accessLevel || '',
        bestPlacetoWorkRating: company?.bestPlacetoWorkRating || '',
        address: company?.address || '',
        country: company?.country || '',
        city: company?.city || '',
        zip: company?.zip || '',
        phone: company?.phone || '',
        legalName: company?.legalName || '',
        vat: company?.vat || '',
        duns: company?.duns || '',
        globalUltimate: company?.globalUltimate || '',
        globalSize: company?.globalSize || '',
        localSize: company?.localSize || '',
        rating: company?.rating || '',
        ratingSource: company?.ratingSource || '',
        businessPurpose: company?.businessPurpose || '',
        localActivities: company?.localActivities || '',
        localStaffing: company?.localStaffing || '',
        businessTypeOther: company?.businessTypeOther || '',
        isFortune: !!company?.isFortune,
        fortuneRank: company?.fortuneRank || '',
        fortuneYear: company?.fortuneYear || '',
        fortuneRevenue: company?.fortuneRevenue || '',
        fortunePercentChange: company?.fortunePercentChange || '',
        fortuneProfit: company?.fortuneProfit || '',
        accountApprovalStatus: company?.accountApprovalStatus || '',
    };
    const urlInput = company?.url || '';

    const latitude = Number(company?.latitude);
    const longitude = Number(company?.longitude);
    const hasPrimaryCoordinates = !Number.isNaN(latitude) && !Number.isNaN(longitude);

    const companyPath = actualCityId
        ? (isCountryCityContext
            ? `/discovery/countries/${countryCode}/cities/${actualCityId}/companies/${actualCompanyId}`
            : `/discovery/cities/${actualCityId}/companies/${actualCompanyId}`)
        : (isCountryOnlyContext
            ? `/discovery/countries/${countryCode}/companies/${actualCompanyId}`
            : `/discovery/companies/${actualCompanyId}`);

    const discoveryCompanyNavItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: 'Jobs' },
    ];

    // Mobile-only: same routes/active-tab logic as discoveryCompanyNavItems,
    // with the Jobs count surfaced as a badge instead of plain text.
    const mobileCompanyNavItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: <JobsTabLabel jobsCount={jobsCount} /> },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-company',
        discoveryType: 'companies',
        companyId: actualCompanyId,
        companyName: company?.title || company?.name || '',
        cityName: cityData?.cityName || cityData?.name || company?.cityRegion || '',
        countryName: countryData?.countryName || countryData?.name || '',
        activeTab: 'General info',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'companies',
            companyName: company?.title || company?.name || '',
            selectedCity: cityData?.cityName || cityData?.name || company?.cityRegion || '',
            selectedCountry: countryData?.countryName || countryData?.name || company?.country || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!company) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">
                                    Company not found.
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!isApproved) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">
                                    This company is not available for discovery.
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb
                                fromCountry={isCountryCityContext ? countryCode : null}
                                countryName={
                                    isCountryCityContext ? cityData?.country?.name :
                                    isCountryOnlyContext ? (countryData?.countryName || countryData?.name) :
                                    null
                                }
                                cityName={actualCityId ? cityData?.name : null}
                                subPage={companyId ? "Companies" : null}
                                companyName={!companyId ? (company?.title || company?.name) : null}
                                label={companyId ? company?.title : null}
                            />
                        </Col>
                    </Row>
                    {!isMobile ? (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        {/* CompanyAddGeneral */}
                                                        <div>
                                                            <Card className="card-premium">
                                                                <Card.Body>
                                                                    <Row className="align-items-center3">
                                                                        <Col>
                                                                            <h3 className="text-start">1. General Info</h3>
                                                                            <hr />
                                                                        </Col>
                                                                    </Row>
                                                                    <>
                                                                        <Row className="mb-4">
                                                                            <Col md={8} className="d-flex">
                                                                                <div className="upload-image-wrapper">
                                                                                    <Form.Group controlId="companyLogo">
                                                                                        <div className="upload-preview not-editing">
                                                                                            <Image
                                                                                                src={logo?.trim() ? logo : icImgPreview}
                                                                                                alt={formData.title || 'Company Logo'}
                                                                                                roundedCircle
                                                                                                className="preview-image"
                                                                                                onError={(e) => {
                                                                                                    e.currentTarget.src = icImgPreview;
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    </Form.Group>
                                                                                </div>
                                                                                <div className="ms-3">
                                                                                    <h1>{formData.title || '—'}</h1>
                                                                                    <p>
                                                                                        {urlInput ? (
                                                                                            <a
                                                                                                href={urlInput.startsWith('http') ? urlInput : `https://${urlInput}`}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                onClick={() => trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                                    companyId: company?._id || actualCompanyId,
                                                                                                    companyName: company?.title || '',
                                                                                                    urlType: 'website',
                                                                                                    level: 2,
                                                                                                    source: isCountryCityContext
                                                                                                        ? 'country_city_company_detail_page'
                                                                                                        : isCountryOnlyContext
                                                                                                            ? 'country_company_detail_page'
                                                                                                            : actualCityId
                                                                                                                ? 'city_company_detail_page'
                                                                                                                : 'direct_company_detail_page',
                                                                                                })}
                                                                                            >
                                                                                                {urlInput}
                                                                                            </a>
                                                                                        ) : (
                                                                                            '—'
                                                                                        )}
                                                                                        {formData.accessLevel && (
                                                                                            <>
                                                                                                <span className="dot"></span>
                                                                                                Access Level: &nbsp;<strong>{formData.accessLevel}</strong>
                                                                                            </>
                                                                                        )}
                                                                                    </p>
                                                                                    <div className="status-wrapper">
                                                                                        <p>
                                                                                            <span className={
                                                                                                formData.accountApprovalStatus?.toLowerCase() === "approved" ? "approved" :
                                                                                                formData.accountApprovalStatus?.toLowerCase() === "rejected" ? "rejected" :
                                                                                                formData.accountApprovalStatus?.toLowerCase() === "lifecycle review" ? "lifecycle-review" :
                                                                                                formData.accountApprovalStatus?.toLowerCase() === "under review" ? "under-review" : ""
                                                                                            }>
                                                                                                <span className="d-flex align-items-center gap-2">
                                                                                                    {formData.accountApprovalStatus?.toLowerCase() === "approved" && <img src={icApproved} alt="Approved" />}
                                                                                                    {formData.accountApprovalStatus?.toLowerCase() === "rejected" && <img src={icRejected} alt="Rejected" />}
                                                                                                    {formData.accountApprovalStatus?.toLowerCase() === "lifecycle review" && <img src={icRLifecycleReview} alt="Lifecycle Review" />}
                                                                                                    {formData.accountApprovalStatus?.toLowerCase() === "under review" && <img src={icUnderReview} alt="Under Review" />}
                                                                                                    {formData.accountApprovalStatus}
                                                                                                </span>
                                                                                            </span>
                                                                                            <span 
                                                                                                className={`badge ${formData.activityStatus ? 'bg-active' : 'bg-inactive'}`}
                                                                                            >
                                                                                                {formData.activityStatus ? 'Active' : 'Inactive'}
                                                                                            </span>
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </Col>

                                                                            <Col md={4} className="d-flex justify-content-end align-items-center">
                                                                                {parseFloat(formData.bestPlacetoWorkRating) > 89 && (() => {
                                                                                    const rating = parseFloat(formData.bestPlacetoWorkRating || 0);
                                                                                    const redDash = rating;
                                                                                    const grayDash = 100 - rating;

                                                                                    return (
                                                                                        <div className="gptw-badge d-flex align-items-center">
                                                                                            <div className="gptw-logo">
                                                                                                <Image src={gptwBanner} alt="aBROADIFY" title="aBROADIFY" />
                                                                                            </div>

                                                                                            <div className="gptw-text-circle">
                                                                                                <div className="gptw-circle position-relative">
                                                                                                    <svg width="80" height="80" viewBox="0 0 36 36">
                                                                                                        <circle
                                                                                                            cx="18"
                                                                                                            cy="18"
                                                                                                            r="16"
                                                                                                            fill="none"
                                                                                                            stroke="#ec1c24"
                                                                                                            strokeWidth="4"
                                                                                                            strokeDasharray={`${redDash} 100`}
                                                                                                            strokeDashoffset="0"
                                                                                                            transform="rotate(-90 18 18)"
                                                                                                        />

                                                                                                        <circle
                                                                                                            cx="18"
                                                                                                            cy="18"
                                                                                                            r="16"
                                                                                                            fill="none"
                                                                                                            stroke="#d0d3d4"
                                                                                                            strokeWidth="4"
                                                                                                            strokeDasharray={`${grayDash} 100`}
                                                                                                            strokeDashoffset={`-${redDash}`}
                                                                                                            transform="rotate(-90 18 18)"
                                                                                                            strokeLinecap="square"
                                                                                                        />
                                                                                                    </svg>

                                                                                                    <div className="gptw-percentage position-absolute top-50 start-50 translate-middle fw-bold">
                                                                                                        {`${Math.round(rating)}%`}
                                                                                                    </div>
                                                                                                </div>

                                                                                                <div className="gptw-text">
                                                                                                    <span className="fw-bold text-dark">
                                                                                                        Employees say<br />
                                                                                                        this is a great<br />
                                                                                                        place to work
                                                                                                    </span>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </Col>
                                                                        </Row>
                                                                    </>
                                                                </Card.Body>
                                                            </Card>
                                                        </div>

                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={discoveryCompanyNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col><h2>Company info</h2></Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        {/* CompanyStats  */}
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col>
                                                        {/* CompanyAddAbout */}
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">1.</span> About Company</h3>
                                                                    </Col>
                                                                </Row>

                                                                {hasPrimaryCoordinates && (
                                                                    <Row className="map-row mb-4">
                                                                        <Col md={12} className="p-0">
                                                                            <MapGL
                                                                                initialViewState={{
                                                                                    latitude,
                                                                                    longitude,
                                                                                    zoom: 15,
                                                                                }}
                                                                                mapStyle="mapbox://styles/mapbox/light-v10"
                                                                                mapboxAccessToken={MAPBOX_TOKEN}
                                                                                style={{ width: '100%', height: 400 }}
                                                                                dragPan={true}
                                                                                scrollZoom={true}
                                                                                doubleClickZoom={true}
                                                                                touchZoomRotate={true}
                                                                            >
                                                                                <Marker
                                                                                    latitude={latitude}
                                                                                    longitude={longitude}
                                                                                >
                                                                                    <div className="marker-bg">
                                                                                        <img
                                                                                            src={logo?.trim() ? logo : icImgPreview}
                                                                                            alt={formData.title || 'Company'}
                                                                                        />
                                                                                    </div>
                                                                                </Marker>
                                                                            </MapGL>
                                                                        </Col>
                                                                    </Row>
                                                                )}
                                                                <>
                                                                    <Row className="mb-4">
                                                                        <Col md={12}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icAdress}
                                                                                        alt="Address"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Address:
                                                                                    <br />
                                                                                    <strong>{formData.address?.split(',')[0]?.trim() || '—'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row className="mb-4">
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icCountry}
                                                                                        alt="Country"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Country:
                                                                                    <br />
                                                                                    <strong>{formData.country || '—'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icCity}
                                                                                        alt="City"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    City:
                                                                                    <br />
                                                                                    <strong>{formData.city || '—'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icCity}
                                                                                        alt="Zip code"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Zip code:
                                                                                    <br />
                                                                                    <strong>{formData.zip || '—'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icPhone}
                                                                                        alt="Phone"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Phone:
                                                                                    <br />
                                                                                    <strong>{formData.phone || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col>
                                                        {/* CompanyAddLegal */}
                                                        <Card className="mb-4">
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">2.</span> Legal Info</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>
                                                                <>
                                                                    <Row className="mb-4">
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icLegalName}
                                                                                        alt="Legal Name"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Legal Name:
                                                                                    <br />
                                                                                    <strong>{formData.legalName || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icNumber}
                                                                                        alt="VAT Number"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    VAT Number:
                                                                                    <br />
                                                                                    <strong>{formData.vat || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icNumber}
                                                                                        alt="Duns"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Duns:
                                                                                    <br />
                                                                                    <strong>{formData.duns || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icNumber}
                                                                                        alt="Global ultimate"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Global ultimate:
                                                                                    <br />
                                                                                    <strong>{formData.globalUltimate || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            </Card.Body>
                                                        </Card>
                                                        {/* CompanyAddSize */}
                                                        <Card>
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">3.</span> Size</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>
                                                                <>
                                                                    <Row className="mb-4">
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icGlobalSize}
                                                                                        alt="Global company size"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Global company size:
                                                                                    <br />
                                                                                    <strong>{formData.globalSize || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icLocalSize}
                                                                                        alt="Local Company Size"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Local Company Size:
                                                                                    <br />
                                                                                    <strong>{formData.localSize || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icRating}
                                                                                        alt="Company Rating"
                                                                                    />
                                                                                </span>
                                                                                <span className="text rating">
                                                                                    Company Rating:
                                                                                    <br />
                                                                                    {Array.from({ length: 5 }).map((_, index) => (
                                                                                        <Image
                                                                                            key={index}
                                                                                            className="rating-star"
                                                                                            src={index < Number(formData.rating || 0) ? icStar : icStarGray}
                                                                                            alt=""
                                                                                        />
                                                                                    ))}

                                                                                    <span className="rating-text">
                                                                                        ({formData.rating || 0} star)
                                                                                    </span>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image
                                                                                        src={icRatingSource}
                                                                                        alt="Company Rating Source"
                                                                                    />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Company Rating Source:
                                                                                    <br />
                                                                                    <strong>{formData.ratingSource || 'N/A'}</strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col>
                                                        {/* CompanyAddIndustries */}
                                                        <Card>
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">4.</span> Industries</h3>
                                                                        <hr />

                                                                        {isLoadingNace && (
                                                                            <div className="text-muted small mb-3">Loading industries…</div>
                                                                        )}

                                                                        {!isLoadingNace && naceError && (
                                                                            <div className="text-danger small mb-3">{naceError}</div>
                                                                        )}
                                                                    </Col>
                                                                </Row>

                                                                {!industriesList.length ? (
                                                                    <div className="text-muted">No industries available.</div>
                                                                ) : (
                                                                    industriesList.map((industry, index) => (
                                                                        <React.Fragment key={index}>
                                                                            {industry.industry && (
                                                                                <>
                                                                                    <Row>
                                                                                        <Col md={12}>
                                                                                            <Card>
                                                                                                <Card.Body className="p-3">
                                                                                                    <div className="icon-text">
                                                                                                        <span className="icon" aria-hidden="true">
                                                                                                            <Image src={icIndustry} alt="Industry" />
                                                                                                        </span>
                                                                                                        <span className="text">
                                                                                                            Industry:
                                                                                                            <br />
                                                                                                            <strong>
                                                                                                                {industryNameByCode.get(industry.industry) || 'N/A'}
                                                                                                            </strong>
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </Card.Body>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    </Row>

                                                                                    <Row className="sub-card-row">
                                                                                        <Col md={12}>
                                                                                            <Card>
                                                                                                <Card.Body>
                                                                                                    <div className="icon-text">
                                                                                                        <span className="icon" aria-hidden="true">
                                                                                                            <Image src={icIndustry} alt="Sub industry" />
                                                                                                        </span>
                                                                                                        <span className="text">
                                                                                                            Sub industry:
                                                                                                            <br />
                                                                                                            <strong>
                                                                                                                {getSubIndustryNames(
                                                                                                                    industry.industry,
                                                                                                                    industry.subIndustry
                                                                                                                )}
                                                                                                            </strong>
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </Card.Body>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    </Row>
                                                                                </>
                                                                            )}
                                                                        </React.Fragment>
                                                                    ))
                                                                )}
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col>
                                                        {/* CompanyAddBusinessType */}
                                                        <Card>
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">5.</span>Business Type</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>
                                                                <>
                                                                    <Row className="mb-3">
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icBusinessPurpose} alt="Business Purpose" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Business Purpose:
                                                                                    <br />
                                                                                    <strong>
                                                                                        {formData.businessPurpose || 'N/A'}
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icLocalActivities} alt="Local Activities" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Local Activities:
                                                                                    <br />
                                                                                    <strong>
                                                                                        {formData.localActivities || 'N/A'}
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icLocalStaffing} alt="Local staffing details" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Local staffing details:
                                                                                    <br />
                                                                                    <strong>
                                                                                        {formData.localStaffing || 'N/A'}
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <div className="icon-text">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icBusinessPurpose} alt="Other" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Other:
                                                                                    <br />
                                                                                    <strong>
                                                                                        {formData.businessTypeOther || 'N/A'}
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        {/* fortune */}
                                                        <Card>
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">6.</span>Fortune 1000</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>
                                                                <>
                                                                    {formData.isFortune ? (
                                                                        <Row className="mb-3">
                                                                            <Col md={2}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icRank} alt="Rank" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Rank:
                                                                                        <br />
                                                                                        <strong>
                                                                                            {formData.fortuneRank || 'N/A'}
                                                                                        </strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col md={2}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icYear} alt="Year" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Year:
                                                                                        <br />
                                                                                        <strong>
                                                                                            {formData.fortuneYear || 'N/A'}
                                                                                        </strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col md={2}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icRevenue} alt="Revenue" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Revenue:
                                                                                        <br />
                                                                                        <strong>
                                                                                            {formData.fortuneRevenue || 'N/A'}
                                                                                        </strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icPercentage} alt="Revenue percent change" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Revenue percent change:
                                                                                        <br />
                                                                                        <strong>
                                                                                            {formData.fortunePercentChange || 'N/A'}
                                                                                        </strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icProfit} alt="Profit" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Profit:
                                                                                        <br />
                                                                                        <strong>
                                                                                            {formData.fortuneProfit || 'N/A'}
                                                                                        </strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                    ) : (
                                                                        <div className="text-muted">This company is not listed as a Fortune 1000 company.</div>
                                                                    )}
                                                                </>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    ) : (
                        <div className="company-detail-mobile">
                            <CompanyDetailHero
                                company={company}
                                isAdminViewer={isAdminViewer}
                                onWebsiteClick={() => trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                    companyId: company?._id || actualCompanyId,
                                    companyName: company?.title || '',
                                    urlType: 'website',
                                    level: 2,
                                    source: isCountryCityContext
                                        ? 'country_city_company_detail_page'
                                        : isCountryOnlyContext
                                            ? 'country_company_detail_page'
                                            : actualCityId
                                                ? 'city_company_detail_page'
                                                : 'direct_company_detail_page',
                                })}
                            />

                            <CompanyDetailMobileTabs items={mobileCompanyNavItems} />

                            <Card className="company-detail-content-card mb-4">
                                <Card.Body>
                                    <h2 className="company-detail-content-title">Company info</h2>

                            {(hasPrimaryCoordinates || hasAnyValue([formData.address, formData.country, formData.city, formData.zip, formData.phone])) && (
                                <CompanyDetailSection title="About company" number={1} showSeparator={false} className="company-detail-about-section">
                                    {hasPrimaryCoordinates && (
                                        <div className="map-row company-detail-about-map">
                                            <MapGL
                                                initialViewState={{ latitude, longitude, zoom: 15 }}
                                                mapStyle="mapbox://styles/mapbox/light-v10"
                                                mapboxAccessToken={MAPBOX_TOKEN}
                                                style={{ width: '100%', height: 260 }}
                                                dragPan={true}
                                                scrollZoom={false}
                                                doubleClickZoom={true}
                                                touchZoomRotate={true}
                                            >
                                                <Marker latitude={latitude} longitude={longitude}>
                                                    <div className="marker-bg">
                                                        <img src={logo?.trim() ? logo : icImgPreview} alt={formData.title || 'Company'} />
                                                    </div>
                                                </Marker>
                                            </MapGL>
                                        </div>
                                    )}
                                    <InfoRow icon={icAdress} iconAlt="Address" label="Address" value={formData.address?.split(',')[0]?.trim()} />
                                    <InfoRow icon={icCountry} iconAlt="Country" label="Country" value={formData.country} />
                                    <InfoRow icon={icCity} iconAlt="City" label="City" value={formData.city} />
                                    <InfoRow icon={icCity} iconAlt="Zip code" label="Zip code" value={formData.zip} />
                                    <InfoRow icon={icPhone} iconAlt="Phone" label="Phone" value={formData.phone} href={formData.phone ? `tel:${formData.phone}` : undefined} external={false} />
                                </CompanyDetailSection>
                            )}

                            {hasAnyValue([formData.legalName, formData.vat, formData.duns, formData.globalUltimate]) && (
                                <CompanyDetailSection title="Legal info" number={2}>
                                    <InfoRow icon={icLegalName} iconAlt="Legal Name" label="Legal Name" value={formData.legalName} />
                                    <InfoRow icon={icNumber} iconAlt="VAT Number" label="VAT Number" value={formData.vat} />
                                    <InfoRow icon={icNumber} iconAlt="Duns" label="Duns" value={formData.duns} />
                                    <InfoRow icon={icNumber} iconAlt="Global ultimate" label="Global ultimate" value={formData.globalUltimate} />
                                </CompanyDetailSection>
                            )}

                            {hasAnyValue([formData.globalSize, formData.localSize, company?.rating]) && (
                                <CompanyDetailSection title="Size" number={3}>
                                    <InfoRow icon={icGlobalSize} iconAlt="Global company size" label="Global company size" value={formData.globalSize} />
                                    <InfoRow icon={icLocalSize} iconAlt="Local company size" label="Local company size" value={formData.localSize} />
                                    {!isEmptyValue(company?.rating) && (
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icRating} alt="Company Rating" />
                                            </span>
                                            <span className="text">
                                                Company rating
                                                <br />
                                                <StarRating rating={company?.rating} />
                                                {formData.ratingSource && (
                                                    <span className="company-detail-rating-source">Source: {formData.ratingSource}</span>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </CompanyDetailSection>
                            )}

                            {industriesList.length > 0 && (
                                <CompanyDetailSection title="Industries" number={4}>
                                    {industriesList.map((industry, index) => (
                                        industry.industry && (
                                            <div key={index} className="company-detail-industry-row">
                                                <strong>{industryNameByCode.get(industry.industry) || industry.industry}</strong>
                                                <div className="text-muted">
                                                    {getSubIndustryNames(industry.industry, industry.subIndustry)}
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </CompanyDetailSection>
                            )}

                            {hasAnyValue([formData.businessPurpose, formData.localActivities, formData.localStaffing, formData.businessTypeOther]) && (
                                <CompanyDetailSection title="Business type" number={5}>
                                    <InfoRow icon={icBusinessPurpose} iconAlt="Business Purpose" label="Business Purpose" value={formData.businessPurpose} />
                                    <InfoRow icon={icLocalActivities} iconAlt="Local Activities" label="Local Activities" value={formData.localActivities} />
                                    <InfoRow icon={icLocalStaffing} iconAlt="Local staffing details" label="Local staffing details" value={formData.localStaffing} />
                                    <InfoRow icon={icBusinessPurpose} iconAlt="Other" label="Other" value={formData.businessTypeOther} />
                                </CompanyDetailSection>
                            )}

                            {formData.isFortune && (
                                <CompanyDetailSection title="Fortune 1000" number={6}>
                                    <div className="company-detail-fortune-grid">
                                        <InfoRow icon={icRank} iconAlt="Rank" label="Rank" value={company?.fortuneRank} />
                                        <InfoRow icon={icYear} iconAlt="Year" label="Year" value={company?.fortuneYear} />
                                        <InfoRow icon={icRevenue} iconAlt="Revenue" label="Revenue" value={company?.fortuneRevenue} />
                                        <InfoRow icon={icPercentage} iconAlt="Revenue percent change" label="Revenue percent change" value={company?.fortunePercentChange} />
                                        <InfoRow icon={icProfit} iconAlt="Profit" label="Profit" value={company?.fortuneProfit} />
                                    </div>
                                </CompanyDetailSection>
                            )}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Container>
            </main>
        </>
    );
}

export default DiscoveryCompanyView;
